import { BarChart3 } from 'lucide-react';
import React, { useState } from 'react';
import { User, Target, Users, FileText, Trophy } from 'lucide-react';
import { Button } from './Button';
import Input from './Input';
import Select from './Select';
import TextArea from './TextArea';
import CameraCapture from './CameraCapture';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FormularioInforme as FormularioInformeType, 
  initialFormularioInforme 
} from '../../types/informe';
import { 
  Lateralidad, 
  Nivel, 
  Posicion, 
  LetraEquipo,
  Categoria,
  Competicion,
  CategoriaFutura,
  getLateralidades,
  getNiveles,
  getPosiciones,
  getLetrasEquipo,
  getCategorias,
  getCompeticiones,
  getCategoriasFuturas,
  getLateralidadLabel,
  getNivelLabel,
  getPosicionLabel,
  getLetraEquipoLabel,
  getCategoriaLabel,
  getCompeticionLabel,
  getCategoriaFuturaLabel,
  getAñosDisponibles
} from '../../types/enums';

interface FormularioInformeProps {
  /** Función que se ejecuta al enviar el formulario */
  onSubmit: (data: FormularioInformeType) => void;
  /** Si el formulario está en proceso de envío */
  isLoading?: boolean;
  /** Datos iniciales del formulario */
  initialData?: Partial<FormularioInformeType>;
  /** Clase CSS adicional */
  className?: string;
  /** Texto personalizado para el botón de envío */
  submitButtonText?: string;
  /** Si mostrar información del partido */
  showPartidoInfo?: boolean;
  /** Si es un informe sin partido (para mostrar campos adicionales) */
  sinPartido?: boolean;
}

/**
 * Componente de formulario para crear informes de jugadores
 */
const FormularioInforme: React.FC<FormularioInformeProps> = ({
  onSubmit,
  isLoading = false,
  initialData = {},
  className = '',
  submitButtonText = 'Crear Informe',
  sinPartido = false
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormularioInformeType>({
    ...initialFormularioInforme,
    ...initialData
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Actualiza un campo del formulario
   */
  const updateField = (field: keyof FormularioInformeType, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  /**
   * Valida el formulario antes del envío
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validaciones requeridas
    if (!formData.nombreCompleto.trim()) {
      newErrors.nombreCompleto = 'El nombre completo es requerido';
    }

    if (!formData.nombreEquipo.trim()) {
      newErrors.nombreEquipo = 'El nombre del equipo es requerido';
    }

    if (!formData.letraEquipo) {
      newErrors.letraEquipo = 'La letra del equipo es requerida';
    }

    if (!formData.lateralidad) {
      newErrors.lateralidad = 'La lateralidad es requerida';
    }

    if (!formData.nivel) {
      newErrors.nivel = 'El nivel es requerido';
    }

    if (!formData.posicion) {
      newErrors.posicion = 'La posición es requerida';
    }

    if (!formData.año) {
      newErrors.año = 'El año de nacimiento es requerido';
    }

    if (!formData.dorsal || formData.dorsal < 1 || formData.dorsal > 99) {
      newErrors.dorsal = 'El dorsal es obligatorio y debe estar entre 1 y 99';
    }

    if (!formData.categoriaFutura) {
      newErrors.categoriaFutura = 'La categoría futura es obligatoria';
    }

    // Validaciones adicionales para informes sin partido
    if (sinPartido) {
      if (!formData.categoriaEquipo) {
        newErrors.categoriaEquipo = 'La categoría es requerida';
      }
      
      if (!formData.grupoEquipo) {
        newErrors.grupoEquipo = 'El grupo es requerido';
      }
    }

    // Validación de año
    if (formData.año) {
      const añosDisponibles = getAñosDisponibles();
      if (!añosDisponibles.includes(Number(formData.año))) {
        newErrors.año = `El año debe estar entre ${Math.min(...añosDisponibles)} y ${Math.max(...añosDisponibles)}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Inyectar la sección del usuario si existe
      const dataToSend = {
        ...formData,
        seccion: user?.seccion
      };
      onSubmit(dataToSend);
    }
  };

  /**
   * Maneja la selección de imagen
   */
  const handleImageSelect = (file: File | null) => {
    updateField('foto', file);
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${className}`}>
      {/* Información del Jugador */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <User size={20} />
          Información del Jugador
        </h3>
        
        <Input
          label="Nombre Completo"
          value={formData.nombreCompleto}
          onChange={(e) => updateField('nombreCompleto', e.target.value)}
          placeholder="Ej: Juan Pérez García"
          error={errors.nombreCompleto}
          required
        />
        
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Nombre del Equipo"
            value={formData.nombreEquipo}
            onChange={(e) => updateField('nombreEquipo', e.target.value)}
            placeholder="Ej: FUE Cornellà"
            error={errors.nombreEquipo}
            required
          />
          
          <Select
            label="Letra del Equipo *"
            value={formData.letraEquipo}
            onChange={(e) => updateField('letraEquipo', e.target.value as LetraEquipo)}
            options={getLetrasEquipo().map(letra => ({
              value: letra,
              label: getLetraEquipoLabel(letra)
            }))}
            error={errors.letraEquipo}
            required
          />
        </div>

        {/* Campos adicionales para informes sin partido */}
        {sinPartido && (
          <>
            <h4 className="text-md font-medium text-white flex items-center gap-2 mt-6">
              <Trophy size={18} />
              Información de Competición
            </h4>
            
            <Select
              label="Competición"
              value={formData.competicion || ''}
              onChange={(e) => updateField('competicion', e.target.value as Competicion)}
              options={getCompeticiones(user?.seccion).map(competicion => ({
                value: competicion,
                label: getCompeticionLabel(competicion)
              }))}
              error={errors.competicion}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Categoría *"
                value={formData.categoriaEquipo || ''}
                onChange={(e) => updateField('categoriaEquipo', e.target.value as Categoria)}
                options={getCategorias(user?.seccion).map(categoria => ({
                  value: categoria,
                  label: getCategoriaLabel(categoria)
                }))}
                error={errors.categoriaEquipo}
                required
              />
              
              <Input
                label="Grupo *"
                type="number"
                value={formData.grupoEquipo || ''}
                onChange={(e) => updateField('grupoEquipo', Number(e.target.value))}
                placeholder="Ej: 1"
                error={errors.grupoEquipo}
                required
                min={1}
                max={100}
              />
            </div>
          </>
        )}
      </div>

      {/* Características del Jugador */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Target size={20} />
          Características
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Lateralidad *"
            value={formData.lateralidad}
            onChange={(e) => updateField('lateralidad', e.target.value as Lateralidad)}
            options={getLateralidades().map(lateralidad => ({
              value: lateralidad,
              label: getLateralidadLabel(lateralidad)
            }))}
            error={errors.lateralidad}
            required
          />
          
          <Select
            label="Nivel *"
            value={formData.nivel}
            onChange={(e) => updateField('nivel', e.target.value as Nivel)}
            options={getNiveles().map(nivel => ({
              value: nivel,
              label: getNivelLabel(nivel)
            }))}
            error={errors.nivel}
            required
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Posición *"
            value={formData.posicion}
            onChange={(e) => updateField('posicion', e.target.value as Posicion)}
            options={getPosiciones(user?.seccion).map(posicion => ({
              value: posicion,
              label: getPosicionLabel(posicion)
            }))}
            error={errors.posicion}
            required
          />
          
          <Select
            label="Año de Nacimiento *"
            value={formData.año}
            onChange={(e) => updateField('año', e.target.value)}
            options={getAñosDisponibles(user?.seccion).map(año => ({
              value: año,
              label: año.toString()
            }))}
            error={errors.año}
            required
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Dorsal"
            type="number"
            min="1"
            max="99"
            value={formData.dorsal?.toString() || ''}
            onChange={(e) => updateField('dorsal', parseInt(e.target.value) || undefined)}
            placeholder="1-99"
            error={errors.dorsal}
            required
          />
          
          <Select
            label="Categoría Futura *"
            value={formData.categoriaFutura || ''}
            onChange={(e) => updateField('categoriaFutura', e.target.value as CategoriaFutura)}
            options={getCategoriasFuturas(user?.seccion).map(categoria => ({
              value: categoria,
              label: getCategoriaFuturaLabel(categoria)
            }))}
            error={errors.categoriaFutura}
            required
          />
        </div>
      </div>

      {/* Valoración de Habilidades */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <BarChart3 size={20} />
          Valoración de Habilidades (1-4)
        </h3>
        
        <div className="bg-secondary-800 p-4 rounded-lg space-y-6">
          {['potenciaFisica', 'velocidad', 'tomaDecision', 'chute', 'tecnicaIndividual'].map((skill) => (
            <div key={skill} className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-secondary-200 capitalize">
                  {skill.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                  (formData[skill as keyof FormularioInformeType] as number) >= 3 ? 'bg-green-500/20 text-green-400' :
                  (formData[skill as keyof FormularioInformeType] as number) === 2 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {formData[skill as keyof FormularioInformeType] as number}
                </span>
              </div>
              
              {/* Botones de valoración 1-4 */}
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((valor) => (
                  <button
                    key={valor}
                    type="button"
                    onClick={() => updateField(skill as keyof FormularioInformeType, valor)}
                    className={`flex-1 h-10 rounded-lg font-bold transition-all ${
                      (formData[skill as keyof FormularioInformeType] as number) === valor
                        ? valor >= 3 
                          ? 'bg-green-500 text-white' 
                          : valor === 2 
                            ? 'bg-yellow-500 text-white' 
                            : 'bg-red-500 text-white'
                        : 'bg-secondary-700 text-secondary-400 hover:bg-secondary-600'
                    }`}
                  >
                    {valor}
                  </button>
                ))}
              </div>
              
              <div className="flex justify-between text-xs text-secondary-400 px-1">
                <span>Bajo</span>
                <span>Alto</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Observaciones */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FileText size={20} />
          Observaciones
        </h3>
        
        <TextArea
          label="Comentarios adicionales"
          value={formData.observaciones}
          onChange={(e) => updateField('observaciones', e.target.value)}
          placeholder="Escribe aquí cualquier observación sobre el jugador..."
          rows={4}
        />
      </div>

      {/* Foto del Jugador */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Users size={20} />
          Foto del Jugador
        </h3>
        
        <CameraCapture
          onImageSelect={handleImageSelect}
          currentImage={formData.foto}
          buttonText="Añadir Foto del Jugador"
          disabled={isLoading}
        />
      </div>

      {/* Botón de Envío */}
      <div className="pt-6 pb-8">
        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
        >
          {isLoading ? 'Creando Informe...' : submitButtonText}
        </Button>
      </div>
    </form>
  );
};

export default FormularioInforme;